import { connect, ConnectedProps } from 'react-redux';
import { useNavigate } from 'react-router-dom-v5-compat';

import { MessageType } from 'dashboard/lib/interfaces/Message';
import { GraphAttributes } from 'dashboard/models/graph';
import { GraphDatumType, GraphDatumTypeAttributes } from 'dashboard/models/graph-datum-type';
import { showMessage } from 'dashboard/redux/actions/messages';
import { RootState } from 'dashboard/redux/store';
import { loadAccountTypes } from 'dashboard/redux/thunk-actions/account-types';
import { loadGraphsAndGraph, updateGraph } from 'dashboard/redux/thunk-actions/graph';
import { createGraphDatumType } from 'dashboard/redux/thunk-actions/graph-datum-type';
import DashboardGraphConfigView from 'dashboard/views/DashboardGraphConfigView';
import useApiCallback from 'lib/hooks/useApiCallback';
import useApiErrorHandler from 'lib/hooks/useApiErrorHandler';
import useApiLoader from 'lib/hooks/useApiLoader';
import { isPermissionsError, permissionsErrorMessage } from 'lib/permissions-errors';

const DashboardGraphConfigRoute = (props: PropsFromRedux) => {
  const { accountTypes, currentEnvironment, graph } = props;
  const navigate = useNavigate();

  const { loading } = useApiLoader({
    loader: () => props.loadGraphsAndGraph(currentEnvironment.id),
    deps: [currentEnvironment],
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage(
          'Failed to load graph configuration, please reload to try again',
          MessageType.Failure,
        );
      }
    },
    onNetworkError: () => {
      props.showMessage(
        'We are experiencing network issues. Please reload to try again.',
        MessageType.Failure,
      );
    },
  });

  const {
    loading: isSubmittingForm,
    error: handleUpdateGraphError,
    callback: handleUpdateGraph,
  } = useApiCallback(
    async (values: GraphAttributes) => {
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      await props.updateGraph(graph.id, values);
      props.showMessage('Successfully updated Graph configuration.', MessageType.Success);
    },
    [graph],
  );

  useApiErrorHandler({
    error: handleUpdateGraphError,
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage(
          'Failed to update Graph configuration, please try again.',
          MessageType.Failure,
          false,
        );
      }
    },
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
      ),
  });

  const {
    loading: isSubmittingNewNodeForm,
    error: handleCreateGraphDatumTypeError,
    callback: handleCreateGraphDatumType,
  } = useApiCallback(
    async (values: GraphDatumTypeAttributes) => {
      await props
        .createGraphDatumType({
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          'graph-token': graph.id,
          ...values,
        })
        .then((graphDatumType: GraphDatumType) => {
          navigate(`/dashboard/graph-configuration/${graphDatumType.id}`);
        });
      props.showMessage('Successfully created new node type.', MessageType.Success);
    },
    [graph, currentEnvironment],
  );

  useApiErrorHandler({
    error: handleCreateGraphDatumTypeError,
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage(
          'Failed to create node type, please try again.',
          MessageType.Failure,
          false,
        );
      }
    },
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
      ),
  });

  const { loading: loadingAccountTypes } = useApiLoader({
    loader: () => props.loadAccountTypes(),
    deps: [currentEnvironment],
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage(
          'Failed to load account types, please reload to try again',
          MessageType.Failure,
        );
      }
    },
    onNetworkError: () => {
      props.showMessage(
        'We are experiencing network issues. Please reload to try again.',
        MessageType.Failure,
      );
    },
  });

  return (
    <DashboardGraphConfigView
      loading={loading || loadingAccountTypes}
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      graph={graph}
      handleGraphConfigSubmit={handleUpdateGraph}
      handleCreateGraphDatumType={handleCreateGraphDatumType}
      isSubmittingForm={isSubmittingForm}
      isSubmittingNewNodeForm={isSubmittingNewNodeForm}
      accountTypes={accountTypes}
    />
  );
};

const mapStateToProps = (state: RootState) => {
  return {
    accountTypes: state.accountTypes.accountTypes,
    currentEnvironment: state.currentEnvironment.currentEnvironment,
    graph: state.graph.graph,
    graphDatumType: state.graphDatumType.graphDatumType,
  };
};

const mapDispatchToProps = {
  loadAccountTypes,
  createGraphDatumType,
  loadGraphsAndGraph,
  updateGraph,
  showMessage,
};

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(DashboardGraphConfigRoute);
